import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddOutlined,
  BookmarkBorderOutlined,
  NotificationsNoneOutlined,
  SearchOutlined,
  ShieldOutlined,
  LightModeOutlined,
  DarkModeOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setAppTheme } from "../../store/slices/blogSlice";
import { listenToNotifications, registerUser } from "../../socket";
import { timeAgo } from "../../utilities/timerFunction";
import {
  deleteNotifications,
  getNotifications,
} from "../../providers/dashboardProvider";
import { profileCheckingApi } from "../../providers/userProvider";
import { setUserDetails } from "../../store/slices/userSlice";
import aapmorLogo from "../../assets/AAPMOR LOGO.svg";
import aapmorlighttext from "../../assets/aapmorwhitetext.svg";
import aapmortext from "../../assets/aapmortext.svg";

const logoStyles = {
  shell: {
    background: "var(--home-warm-white)",
    borderBottom: "1px solid var(--home-border)",
    padding: "0 40px",
    display: "flex",
    alignItems: "center",
    gap: "32px",
    minHeight: "68px",
    position: "sticky",
    top: 0,
    zIndex: 100,
    backdropFilter: "blur(12px)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textDecoration: "none",
    flexShrink: 0,
    cursor: "pointer",
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    // background: "var(--home-ink)",
    border: "1.5px solid var(--home-accent)",
    color: "var(--home-gold)",
    fontFamily: '"DM Serif Display", serif',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "-1px",
  },
  logoText: {
    color: "var(--home-ink)",
    fontFamily: '"DM Serif Display", serif',
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  logoSubText: {
    color: "var(--home-muted)",
    fontFamily: '"DM Sans", sans-serif',
    fontSize: 14,
    fontWeight: 300,
    letterSpacing: "0.08em",
    // textTransform: "uppercase",
  },
};

const resolveProfileImageUrl = (imageUrl = "") => {
  if (!imageUrl) return "";

  if (/^(https?:|data:|blob:)/i.test(imageUrl)) {
    return imageUrl;
  }

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  if (!apiBaseUrl) {
    return imageUrl;
  }

  try {
    return new URL(imageUrl, apiBaseUrl).toString();
  } catch {
    return imageUrl;
  }
};

const Header = ({ setSearchInput = () => {}, setProfile }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.userDetails);
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const storedTheme = window.localStorage.getItem("theme");
    return storedTheme ? JSON.parse(storedTheme) : false;
  });
  const [notifications, setNotifications] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const token = Cookies.get("jwtToken");

  useEffect(() => {
    localStorage.setItem("theme", mode);
    dispatch(setAppTheme(mode));
    document.body.classList.toggle("dark-theme", !!mode);
    document.documentElement.classList.toggle("dark-theme", !!mode);
  }, [dispatch, mode]);

  useEffect(() => {
    const getUserDetail = async () => {
      const response = await profileCheckingApi();

      if (!response) return;

      if (response.status === 202) {
        setProfile(true);
      }

      const currentUser = response?.data?.res || {};
      dispatch(setUserDetails(currentUser));

      if (currentUser?._id) {
        registerUser(currentUser._id);
      }
    };

    if (token) {
      getUserDetail();
    }
  }, [dispatch, setProfile, token]);

  useEffect(() => {
    const getNotificationsData = async () => {
      const response = await getNotifications();

      if (response) {
        setNotifications(response?.data?.notifications || []);
      }
    };

    if (token) {
      getNotificationsData();
      const unsubscribeNotifications = listenToNotifications((data) => {
        setNotifications((prev) => [data, ...prev]);
      });

      return unsubscribeNotifications;
    }

    return undefined;
  }, [token]);

  const initials = useMemo(() => {
    const source = user?.name || Cookies.get("username") || "AA";
    return source
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");
  }, [user]);

  const profileImageSrc = useMemo(
    () => resolveProfileImageUrl(user?.profileImage),
    [user?.profileImage],
  );

  const handleLogout = () => {
    Cookies.remove("jwtToken");
    Cookies.remove("username");
    Cookies.remove("userrole");
    navigate("/login");
  };

  const handleClearNotifications = async () => {
    if (!user?._id) return;

    const response = await deleteNotifications(user._id);
    if (response) {
      setNotifications([]);
    }
  };

  const isRouteActive = (type) => {
    const pathname = location.pathname || "/";

    if (type === "home") return pathname === "/";
    if (type === "saved") return pathname.startsWith("/user/saved");
    if (type === "createblog") return pathname.startsWith("/createblog");
    if (type === "admin") return pathname.startsWith("/admin");
    if (type === "profile") return pathname.startsWith("/user/profile");

    return false;
  };

  const getActionButtonSx = (active = false, hideOnMobile = false) => ({
    width: 36,
    height: 36,
    border: active
      ? "1.5px solid var(--home-accent)"
      : "1.5px solid var(--home-border)",
    background: active
      ? "linear-gradient(135deg, var(--home-accent-light), rgba(201, 168, 76, 0.18))"
      : "var(--home-cream)",
    color: active ? "var(--home-accent)" : "var(--home-ink)",
    boxShadow: active
      ? "0 0 0 1px rgba(var(--accent-primary-rgb), 0.12), 0 8px 24px rgba(var(--accent-primary-rgb), 0.18)"
      : "none",
    display: hideOnMobile ? { xs: "none", sm: "inline-flex" } : "inline-flex",
  });

  const isCreateBlogActive = isRouteActive("createblog");

  return (
    <Box
      sx={{
        ...logoStyles.shell,
        "@media(max-width:767px)": {
          px: "16px",
          py: "12px",
          gap: "14px",
          flexWrap: "wrap",
        },
      }}
    >
      <Box
        sx={{
          ...logoStyles.logo,
          ...(isRouteActive("home")
            ? {
                filter:
                  "drop-shadow(0 10px 18px rgba(var(--accent-primary-rgb), 0.12))",
              }
            : {}),
        }}
        onClick={() => {
          Cookies.set("selectedTab", "home");
          navigate("/");
        }}
      >
        <Box sx={logoStyles.logoMark}>
          <img src={aapmorLogo} alt="AAPMOR Logo" width="20" height="20" />
        </Box>
        <Box sx={logoStyles.logoText}>
          {!mode && (
            <img
              src={aapmortext}
              alt="aapmortext"
              style={{ width: "120px", height: "20px" }}
              width="120"
              height="20"
              loading="eager"
              fetchPriority="high"
              rel="preload"
            />
          )}
          {mode && (
            <img
              src={aapmorlighttext}
              alt="aapmortext"
              style={{ width: "120px", height: "20px" }}
              width="120"
              height="20"
              loading="eager"
              fetchPriority="high"
              rel="preload"
            />
          )}
        </Box>
        <span style={logoStyles.logoSubText}>Blogs</span>
      </Box>

      <Box
        sx={{
          flex: 1,
          maxWidth: 340,
          marginLeft: "auto",
          position: "relative",
          "@media(max-width:767px)": {
            order: 3,
            maxWidth: "100%",
            width: "100%",
          },
        }}
      >
        <SearchOutlined
          sx={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.45,
            fontSize: 18,
            color: "var(--home-ink)",
          }}
        />
        <input
          type="search"
          placeholder="Search articles, authors..."
          onChange={(event) => setSearchInput(event.target.value)}
          style={{
            width: "100%",
            padding: "9px 16px 9px 38px",
            border: "1.5px solid var(--home-border)",
            borderRadius: "100px",
            background: "var(--home-cream)",
            color: "var(--home-ink)",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 13,
            outline: "none",
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          "@media(max-width:767px)": {
            marginLeft: "auto",
            gap: "10px",
          },
        }}
      >
        <Tooltip title={mode ? "Light mode" : "Dark mode"}>
          <IconButton
            onClick={() => setMode(!mode)}
            sx={getActionButtonSx(false)}
          >
            {mode ? (
              <LightModeOutlined
                sx={{ fontSize: 18, color: "var(--home-ink)" }}
              />
            ) : (
              <DarkModeOutlined
                sx={{ fontSize: 18, color: "var(--home-ink)" }}
              />
            )}
          </IconButton>
        </Tooltip>

        {token && (
          <Tooltip title="Notifications">
            <IconButton
              onClick={(event) => setNotificationAnchor(event.currentTarget)}
              sx={getActionButtonSx(false)}
            >
              <Badge badgeContent={notifications.length} color="error">
                <NotificationsNoneOutlined
                  sx={{ fontSize: 18, color: "var(--home-ink)" }}
                />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {token && (
          <Tooltip title="Saved blogs">
            <IconButton
              onClick={() => navigate("/user/saved")}
              sx={getActionButtonSx(isRouteActive("saved"), true)}
            >
              <BookmarkBorderOutlined
                sx={{ fontSize: 18, color: "var(--home-ink)" }}
              />
            </IconButton>
          </Tooltip>
        )}

        {token && !!user?.admin && (
          <Tooltip title="Admin">
            <IconButton
              onClick={() => navigate("/admin")}
              sx={getActionButtonSx(isRouteActive("admin"))}
            >
              <ShieldOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {token && (
          <Avatar
            src={profileImageSrc}
            onClick={() => navigate("/user/profile")}
            alt={`${user?.name || "User"}'s profile`}
            sx={{
              width: 36,
              height: 36,
              cursor: "pointer",
              border: isRouteActive("profile")
                ? "2px solid var(--home-accent)"
                : "2px solid var(--home-border)",
              background:
                "linear-gradient(135deg, var(--home-accent) 0%, var(--accent-deep) 100%)",
              color: "#ffffff",
              fontSize: 13,
              fontWeight: 600,
              boxShadow: isRouteActive("profile")
                ? "0 0 0 3px rgba(var(--accent-primary-rgb), 0.16), 0 10px 28px rgba(var(--accent-primary-rgb), 0.22)"
                : "none",
            }}
            imgProps={{
              loading: "lazy",
              decoding: "async",
            }}
          >
            {initials}
          </Avatar>
        )}

        {token ? (
          <>
            <Button
              variant="contained"
              onClick={() => navigate("/createblog")}
              sx={{
                minWidth: "auto",
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                gap: "6px",
                px: "20px",
                py: "9px",
                borderRadius: "100px",
                border: "1px solid transparent",
                backgroundColor: isCreateBlogActive
                  ? "var(--home-accent)"
                  : mode
                    ? "rgba(255, 255, 255, 0.08)"
                    : "var(--home-ink)",
                backgroundImage: isCreateBlogActive
                  ? "linear-gradient(135deg, var(--home-accent), var(--home-gold))"
                  : mode
                    ? "linear-gradient(135deg, var(--home-accent), var(--accent-deep))"
                    : "linear-gradient(135deg, var(--home-ink), var(--home-ink))",
                color: "#ffffff",
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                boxShadow: isCreateBlogActive
                  ? "0 12px 28px rgba(var(--accent-primary-rgb), 0.28)"
                  : mode
                    ? "0 12px 28px rgba(var(--accent-primary-rgb), 0.22)"
                    : "0 10px 20px rgba(15, 14, 12, 0.12)",
                "&:hover": {
                  backgroundColor: "var(--home-accent)",
                  backgroundImage:
                    "linear-gradient(135deg, var(--home-accent), var(--accent-hover))",
                  color: "#ffffff",
                },
              }}
            >
              <AddOutlined sx={{ fontSize: 16 }} />
              Write
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                minWidth: "auto",
                display: { xs: "none", sm: "inline-flex" },
                px: "16px",
                py: "7px",
                borderRadius: "100px",
                border: "1.5px solid var(--home-border)",
                color: "var(--home-muted)",
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Logout
            </Button>
            {/* <IconButton
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ display: { xs: "inline-flex", sm: "none" } }}
            >
              <Avatar
                src={user?.profileImage}
                sx={{ width: 32, height: 32, fontSize: 12 }}
              >
                {initials}
              </Avatar>
            </IconButton> */}
          </>
        ) : (
          <Button
            onClick={() => navigate("/login")}
            sx={{
              minWidth: "auto",
              px: "16px",
              py: "7px",
              borderRadius: "100px",
              border: "1.5px solid var(--home-border)",
              color: "var(--home-muted)",
              textTransform: "none",
              fontSize: 12,
            }}
          >
            Login
          </Button>
        )}
      </Box>

      <Popover
        open={Boolean(notificationAnchor)}
        anchorEl={notificationAnchor}
        onClose={() => setNotificationAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxHeight: 420,
              p: 2,
              borderRadius: 3,
              border: "1px solid var(--home-border)",
              boxShadow: "0 16px 40px rgba(15, 14, 12, 0.08)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          <Button
            onClick={handleClearNotifications}
            disabled={!notifications.length}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Clear
          </Button>
        </Box>

        {!notifications.length ? (
          <Typography sx={{ py: 2, color: "var(--home-muted)", fontSize: 14 }}>
            You&apos;re all caught up.
          </Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <ListItem
                key={`${notification?.blogId || "notification"}-${index}`}
                component="a"
                href={`/blogs/${notification.blogId}`}
                sx={{
                  display: "block",
                  px: 0,
                  py: 1.25,
                  textDecoration: "none",
                  borderBottom:
                    index === notifications.length - 1
                      ? "none"
                      : "1px solid var(--home-border)",
                }}
              >
                <ListItemText
                  primary={notification.message}
                  secondary={timeAgo(notification.timestamp)}
                  primaryTypographyProps={{
                    color: "var(--home-ink)",
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                  secondaryTypographyProps={{
                    color: "var(--home-muted)",
                    fontSize: 12,
                    mt: 0.4,
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </Box>
  );
};

export default Header;
